<section
  class="employee-number-settings"
  aria-labelledby="employee-number-settings-heading"
>
  <header class="section-header">
    <div>
      <h2 id="employee-number-settings-heading">社員番号管理</h2>
      <p>
        KPI
        入稿時の突合に使用する社員番号を、メールアドレスと対応付けて一括登録します。
      </p>
    </div>
    <button
      mat-stroked-button
      type="button"
      [disabled]="isBusy"
      (click)="downloadTemplate()"
    >
      <mat-icon aria-hidden="true">download</mat-icon>
      テンプレートをダウンロード
    </button>
  </header>

  <section class="import-card" aria-labelledby="employee-number-import-heading">
    <div class="card-heading">
      <div>
        <h3 id="employee-number-import-heading">社員番号をインポート</h3>
        <p>
          「社員番号」「メールアドレス」列を持つ CSV
          を選択すると、取り込み前に結果を確認できます。
        </p>
      </div>
      <span class="format-chip">CSV</span>
    </div>

    <fieldset class="uploader-fieldset" [disabled]="isBusy">
      <legend class="visually-hidden">社員番号ファイルの選択</legend>
      <app-file-uploader
        [acceptFileExtensions]="acceptFileExtensions"
        (uploaded)="onFilesSelected($event)"
      />
    </fieldset>

    <div class="file-status" aria-live="polite">
      @if (isPreviewing) {
        <mat-spinner diameter="18" aria-hidden="true"></mat-spinner>
      } @else {
        <mat-icon aria-hidden="true">description</mat-icon>
      }
      @if (selectedFile) {
        <span>
          {{ selectedFile.name }}（{{ formatFileSize(selectedFile.size) }}）{{
            isPreviewing ? 'をプレビュー中...' : ''
          }}
        </span>
      } @else {
        <span>ファイルは選択されていません（最大10MB）</span>
      }
    </div>

    <div class="primary-actions">
      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="!canConfirmImport"
        (click)="confirmImport()"
      >
        @if (isImporting) {
          <mat-spinner diameter="18" aria-hidden="true"></mat-spinner>
        } @else {
          <mat-icon aria-hidden="true">upload_file</mat-icon>
        }
        {{ isImporting ? '取込中...' : '取り込みを確定' }}
      </button>
    </div>

    @if (importErrorMessage) {
      <p class="operation-error" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        {{ importErrorMessage }}
      </p>
    }

    @if (importOperationMessage) {
      <p class="operation-result" role="status">
        <mat-icon aria-hidden="true">check_circle</mat-icon>
        {{ importOperationMessage }}
      </p>
    }

    @if (confirmedResult) {
      <section class="confirmed-summary" aria-label="社員番号の取込結果">
        <h4>取込結果</h4>
        <p>
          新規 <strong>{{ confirmedResult.newCount }}件</strong>・更新
          <strong>{{ confirmedResult.updatedCount }}件</strong
          >を取り込みました（変更なし
          {{ confirmedResult.unchangedCount }}件・エラー
          {{ confirmedResult.errorCount }}件）。
        </p>
      </section>
    }

    @if (previewResult && previewResult.fileErrors.length === 0) {
      <section
        class="preview-section"
        aria-labelledby="employee-preview-heading"
      >
        <div class="preview-heading">
          <div>
            <h4 id="employee-preview-heading">取込プレビュー</h4>
            <p>エラー行を除く行が確定時の取り込み対象です。</p>
          </div>
          <div class="summary-chips" aria-label="プレビュー集計">
            <span>総行数 {{ previewResult.totalRows }}件</span>
            <span class="new">新規 {{ previewResult.newCount }}件</span>
            <span class="updated">更新 {{ previewResult.updatedCount }}件</span>
            <span>変更なし {{ previewResult.unchangedCount }}件</span>
            <span class="error">エラー {{ previewResult.errorCount }}件</span>
          </div>
        </div>

        <div
          class="table-wrap preview-table-wrap"
          tabindex="0"
          role="region"
          aria-label="取込プレビュー表"
        >
          <table class="preview-table">
            <caption class="visually-hidden">
              社員番号の取込プレビュー
            </caption>
            <thead>
              <tr>
                <th scope="col">行番号</th>
                <th scope="col">社員番号</th>
                <th scope="col">メールアドレス</th>
                <th scope="col">ユーザー名</th>
                <th scope="col">判定</th>
                <th scope="col">メッセージ</th>
              </tr>
            </thead>
            <tbody>
              @for (row of previewResult.rows; track $index) {
                <tr
                  class="preview-row"
                  [class.new-row]="row.status === importStatus.New"
                  [class.error-row]="row.status === importStatus.Error"
                  [attr.data-status]="row.status"
                >
                  <td>
                    @if (
                      row.rowNumber !== null && row.rowNumber !== undefined
                    ) {
                      {{ row.rowNumber }}
                    } @else {
                      —
                    }
                  </td>
                  <td>{{ row.employeeNumber }}</td>
                  <td>{{ row.email }}</td>
                  <td>
                    @if (row.userName) {
                      {{ row.userName }}
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    <span
                      class="status-badge"
                      [class.status-new]="row.status === importStatus.New"
                      [class.status-updated]="
                        row.status === importStatus.Updated
                      "
                      [class.status-unchanged]="
                        row.status === importStatus.Unchanged
                      "
                      [class.status-error]="row.status === importStatus.Error"
                    >
                      {{ statusLabel(row.status) }}
                    </span>
                  </td>
                  <td class="message-cell">
                    @if (row.message) {
                      {{ row.message }}
                    } @else {
                      —
                    }
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td class="empty-cell" colspan="6">
                    プレビュー対象の行はありません。
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (previewResult.omittedRows > 0) {
          <p class="omitted-rows-note" role="note">
            他 {{ previewResult.omittedRows }} 行は省略されました。
          </p>
        }
      </section>
    }
  </section>

  <section class="mapping-card" aria-labelledby="mapping-list-heading">
    <div class="mapping-list-head">
      <div>
        <h3 id="mapping-list-heading">社員番号対照一覧</h3>
        <p>社員番号が未設定のユーザーを先頭に表示します。</p>
      </div>
      <span class="count-chip">{{ countText }}</span>
    </div>

    @if (mappingOperationMessage) {
      <p class="operation-result mapping-result" role="status">
        <mat-icon aria-hidden="true">check_circle</mat-icon>
        {{ mappingOperationMessage }}
      </p>
    }

    @if (isLoading) {
      <div class="status-panel" aria-live="polite">
        <mat-spinner diameter="28" aria-hidden="true"></mat-spinner>
        <span>社員番号の対照一覧を読み込んでいます。</span>
      </div>
    } @else if (loadErrorMessage) {
      <div class="status-panel error-panel" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <span>{{ loadErrorMessage }}</span>
        <button mat-stroked-button type="button" (click)="retry()">
          再試行
        </button>
      </div>
    } @else {
      <label class="mapping-search">
        <mat-icon aria-hidden="true">search</mat-icon>
        <span class="visually-hidden">社員番号対照一覧を検索</span>
        <input
          type="search"
          [formControl]="searchControl"
          placeholder="名前・メールアドレス・社員番号を検索"
          autocomplete="off"
        />
      </label>

      @if (deleteErrorMessage) {
        <p class="operation-error" role="alert">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          {{ deleteErrorMessage }}
        </p>
      }

      <div class="table-wrap mapping-table-wrap">
        <table class="mapping-table">
          <caption class="visually-hidden">
            ユーザーと社員番号の対照一覧
          </caption>
          <thead>
            <tr>
              <th scope="col">名前</th>
              <th scope="col">メールアドレス</th>
              <th scope="col">社員番号</th>
              <th scope="col">操作</th>
            </tr>
          </thead>
          <tbody>
            @for (mapping of filteredMappings; track mapping.userId) {
              <tr
                class="mapping-row"
                [class.unset-row]="isUnset(mapping)"
                [class.inactive-row]="!mapping.userActive"
                [attr.data-user-id]="mapping.userId"
              >
                <td class="name-cell">
                  {{ mapping.userName }}
                  @if (!mapping.userActive) {
                    <span class="inactive-badge">無効ユーザー</span>
                  }
                </td>
                <td>{{ mapping.email }}</td>
                <td>
                  @if (isUnset(mapping)) {
                    <span class="unset-badge">未設定</span>
                  } @else {
                    {{ mapping.employeeNumber }}
                  }
                </td>
                <td class="action-cell">
                  @if (!isUnset(mapping)) {
                    <button
                      mat-stroked-button
                      type="button"
                      class="delete-button"
                      [attr.aria-label]="mapping.userName + 'の社員番号を削除'"
                      [disabled]="isBusy"
                      (click)="requestDelete(mapping)"
                    >
                      @if (deletingUserId === mapping.userId) {
                        <mat-spinner
                          diameter="16"
                          aria-hidden="true"
                        ></mat-spinner>
                        削除中...
                      } @else {
                        <mat-icon aria-hidden="true">delete_outline</mat-icon>
                        削除
                      }
                    </button>
                  } @else {
                    <span class="no-action">—</span>
                  }
                </td>
              </tr>
            } @empty {
              <tr>
                <td class="empty-cell" colspan="4">
                  @if (isSearching) {
                    検索条件に一致するユーザーはいません。
                  } @else {
                    対象ユーザーはいません。
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>
</section>
